/**
 * Pure validation/parsing helpers shared by the realtime agent form fields
 * (Gemini, Grok, and OpenAI realtime variants in SimulatorFormFields).
 * Centralized so the three structurally-similar field groups apply identical
 * timeout/API-key rules and parse user input the same way.
 */

/** Inclusive bounds (in seconds) accepted by the realtime timeout field. */
export const REALTIME_TIMEOUT_MIN_SECONDS = 1
export const REALTIME_TIMEOUT_MAX_SECONDS = 1800

/**
 * Returns the inline error message for an out-of-range timeout, or `undefined`
 * when the value is unset or within the accepted range. An unset
 * (`undefined`) timeout is valid because the field is optional.
 */
export function getTimeoutSecondsError(
  timeoutSeconds: number | undefined,
): string | undefined {
  if (timeoutSeconds === undefined) {
    return undefined
  }
  if (
    timeoutSeconds < REALTIME_TIMEOUT_MIN_SECONDS ||
    timeoutSeconds > REALTIME_TIMEOUT_MAX_SECONDS
  ) {
    return `Timeout must be between ${REALTIME_TIMEOUT_MIN_SECONDS} and ${REALTIME_TIMEOUT_MAX_SECONDS} seconds`
  }
  return undefined
}

/**
 * Parses a raw timeout `<input type="number">` value. Returns the numeric
 * value (including out-of-range values, so the inline error stays visible
 * while the user corrects them) or `undefined` when the field is cleared or
 * non-numeric.
 */
export function parseTimeoutSecondsInput(raw: string): number | undefined {
  const value = parseInt(raw, 10)
  return isNaN(value) ? undefined : value
}

/**
 * Parses a JSON-string of custom SIP headers into a plain object. Returns an
 * empty object for empty input, non-object JSON, or invalid JSON so callers
 * can render the editor without throwing.
 */
export function parseSipHeaders(
  customSipHeaders: string | undefined,
): Record<string, unknown> {
  if (!customSipHeaders) {
    return {}
  }
  try {
    const parsed: unknown = JSON.parse(customSipHeaders)
    return typeof parsed === "object" &&
      parsed !== null &&
      !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

/**
 * Returns the inline error message when a non-empty value is not a valid JSON
 * array, or `undefined` when the value is empty or a valid JSON array. Empty
 * input is valid because the JSON blob fields are optional.
 */
export function getJsonArrayError(
  value: string | undefined,
): string | undefined {
  if (!value?.trim()) {
    return undefined
  }
  try {
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed)) {
      return "Value must be a JSON array"
    }
    return undefined
  } catch {
    return "Value must be valid JSON"
  }
}

/**
 * Returns the inline error message when a non-empty value is not a valid JSON
 * object, or `undefined` when the value is empty or a valid JSON object. Empty
 * input is valid because the JSON blob fields are optional.
 */
export function getJsonObjectError(
  value: string | undefined,
): string | undefined {
  if (!value?.trim()) {
    return undefined
  }
  try {
    const parsed: unknown = JSON.parse(value)
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return "Value must be a JSON object"
    }
    return undefined
  } catch {
    return "Value must be valid JSON"
  }
}
